import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useContactPicker } from '../hooks/useContactPicker';
import { recallTarget, useGmailInvite, type UseGmailInviteResult } from '../hooks/useGmailInvite';
import {
  gmailInviteService,
  GmailInviteBatch,
  GmailInviteStatus,
  InviteProject,
  InviteTarget,
} from '../services/gmailInviteService';

import { InviteReviewStep } from './InviteReviewStep';
import { InviteWhereStep } from './InviteWhereStep';
import { InviteWhoStep } from './InviteWhoStep';

import '../styles/appModal.css';
import './InvitePeople.css';

type Step = 'where' | 'who' | 'review';

interface InvitePeopleModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The project the window was opened from (planning sidebar); otherwise the one worked on last. */
  projectId?: string;
  /** Why connecting Gmail failed, when Google sent the user back with an error. */
  notice?: string | null;
}

const withLastProject = (current: InviteTarget, projects: InviteProject[]): InviteTarget => (
  current.projectId || recallTarget() ? current : { ...current, projectId: projects[0]?.id ?? null }
);

const useTarget = (projectId?: string) => {
  const [projects, setProjects] = useState<InviteProject[] | null>(null);
  const [target, setTarget] = useState<InviteTarget>(
    () => recallTarget() ?? { projectId: projectId ?? null, role: 'reader' },
  );

  useEffect(() => {
    let cancelled = false;

    void gmailInviteService.projects().then((next) => {
      if (cancelled) return;
      setProjects(next);
      setTarget((current) => withLastProject(current, next));
    }).catch(() => setProjects([]));

    return () => { cancelled = true; };
  }, []);

  return { projects, setTarget, target };
};

const STEPS: Array<[Step, string]> = [['where', 'Where'], ['who', 'Who'], ['review', 'Send']];

const StepHeader: React.FC<{ step: Step | 'done'; onClose: () => void }> = ({ step, onClose }) => {
  const at = step === 'done' ? STEPS.length : STEPS.findIndex(([key]) => key === step);

  return (
    <header className="invite-head">
      <p className="am-eyebrow">Invite people</p>
      <ol aria-label="Steps" className="am-segmented invite-head__steps">
        {STEPS.map(([key, name], index) => (
          <li aria-current={index === at ? 'step' : undefined} className={`am-segment${index === at ? ' is-active' : ''}${index < at ? ' is-done' : ''}`} key={key}>
            {index < at ? '✓' : index + 1} {name}
          </li>
        ))}
      </ol>
      <button aria-label="Close" className="am-close" onClick={onClose} type="button">✕</button>
    </header>
  );
};

const Working: React.FC<{ batch: GmailInviteBatch }> = ({ batch }) => {
  const sending = batch.status === 'sending';
  // A dry run (local test mode) builds the invites; it reads the same as prod.
  const done = sending ? (batch.dry_run ? batch.built_count : batch.sent_count + batch.failed_count) : 0;
  const width = sending && batch.invite_count ? `${Math.round((100 * done) / batch.invite_count)}%` : '35%';

  return (
    <section aria-live="polite" className="invite-step">
      <div className="am-header">
        <h3 className="am-title">{sending ? 'Sending your invites' : 'Reading your Google contacts'}</h3>
        <p className="am-lede">
          {sending
            ? `${done} of ${batch.invite_count} sent from your Gmail.`
            : `${batch.contacts_read.toLocaleString()} read so far. This takes a few seconds.`}
        </p>
      </div>
      <div className={`invite-progress${sending ? '' : ' is-indeterminate'}`}><i style={{ width }} /></div>
    </section>
  );
};

interface DoneProps {
  batch: GmailInviteBatch;
  status: GmailInviteStatus | null;
  onMore: () => void;
  onClose: () => void;
}

const doneTitle = (batch: GmailInviteBatch): string => {
  const count = batch.dry_run ? batch.built_count : batch.sent_count;

  const invites = count === 1 ? 'invite' : 'invites';

  return `${count} ${invites} sent`;
};

const Done: React.FC<DoneProps> = ({ batch, status, onMore, onClose }) => (
  <section className="invite-step invite-done">
    <span aria-hidden="true" className="invite-done__mark">✓</span>
    <h3 className="am-title">{doneTitle(batch)}</h3>
    <p className="am-lede">
      {batch.project_id ? 'They join the project when they accept. ' : 'They can join 8080.AI from the link in the email. '}
      {status ? `You can send ${status.remaining_today} more today.` : ''}
    </p>
    {batch.error && <p className="am-error">{batch.error}</p>}
    <div className="am-actions">
      <button className="am-secondary" onClick={onMore} type="button">Invite more</button>
      <button className="am-cta" onClick={onClose} type="button">Done</button>
    </div>
  </section>
);

interface ChoosingProps {
  invite: UseGmailInviteResult;
  batch: GmailInviteBatch;
  status: GmailInviteStatus;
  projects: InviteProject[] | null;
  target: InviteTarget;
  setTarget: (target: InviteTarget) => void;
  step: Step;
  setStep: (step: Step) => void;
  onSent: () => void;
}

const projectName = (projects: InviteProject[] | null, target: InviteTarget): string => (
  projects?.find((project) => project.id === target.projectId)?.title ?? '8080.AI'
);

/** Where, Who and Review while the contact list is held: the ticks survive moving between the steps. */
const Choosing: React.FC<ChoosingProps> = (props) => {
  const { batch, invite, setStep, status, target } = props;
  const picker = useContactPicker(
    batch.id, target.projectId === null, status.remaining_today, status.default_batch_size,
  );
  const name = projectName(props.projects, target);
  const views: Record<Step, () => React.ReactElement> = {
    where: () => (
      <InviteWhereStep next={{ busy: false, label: 'Continue with your contacts', onClick: () => setStep('who') }}
        onChange={props.setTarget} projects={props.projects} target={target} />
    ),
    who: () => (
      <InviteWhoStep general={target.projectId === null} limit={status.remaining_today} onBack={() => setStep('where')}
        onDiscard={() => void invite.discard()} onReview={() => setStep('review')} picker={picker} title={name} />
    ),
    review: () => (
      <InviteReviewStep batchId={batch.id} count={picker.selected.size}
        firstEmail={Array.from(picker.selected)[0] ?? null} onBack={() => setStep('who')}
        onSend={async () => { await invite.send(Array.from(picker.selected), target); props.onSent(); }}
        target={target} targetName={name} />
    ),
  };

  return views[props.step]();
};

type Phase = 'working' | 'done' | 'choosing' | 'start';

const phaseOf = (invite: UseGmailInviteResult, sentHere: boolean): Phase => {
  const { batch, status } = invite;

  if (batch && ['collecting', 'sending'].includes(batch.status)) return 'working';
  if (batch?.status === 'ready' && batch.choosing && status) return 'choosing';

  return sentHere && batch ? 'done' : 'start';
};

/** Everything the window needs: the Gmail flow, the target, the step, and whether this window just sent. */
const useInviteFlow = (projectId: string | undefined, notice: string | null | undefined) => {
  const invite = useGmailInvite(true, notice);
  const { projects, setTarget, target } = useTarget(projectId);
  const [step, setStep] = useState<Step>('who');
  const [sentHere, setSentHere] = useState(false);
  const phase = phaseOf(invite, sentHere);

  return { invite, phase, projects, sentHere, setSentHere, setStep, setTarget, step, target };
};

type InviteFlow = ReturnType<typeof useInviteFlow>;

const headerStep = (flow: InviteFlow): Step | 'done' => {
  if (flow.phase === 'done') return 'done';
  if (flow.phase === 'choosing') return flow.step;

  return flow.phase === 'working' && flow.invite.batch?.status === 'sending' ? 'review' : 'where';
};

const startStep = (flow: InviteFlow) => {
  const { connecting, connect } = flow.invite;
  const label = connecting ? 'Opening Google…' : 'Choose people from Gmail';

  return <InviteWhereStep next={{ busy: connecting, label, onClick: () => void connect(flow.target) }}
    onChange={flow.setTarget} projects={flow.projects} target={flow.target} />;
};

const BODIES: Record<Phase, (flow: InviteFlow, onClose: () => void) => React.ReactElement | null> = {
  working: (flow) => (flow.invite.batch ? <Working batch={flow.invite.batch} /> : null),
  done: (flow, onClose) => (flow.invite.batch ? (
    <Done batch={flow.invite.batch} onClose={onClose} onMore={() => { flow.setSentHere(false); flow.setStep('who'); }}
      status={flow.invite.status} />
  ) : null),
  choosing: (flow) => (flow.invite.batch && flow.invite.status ? (
    <Choosing
      batch={flow.invite.batch}
      invite={flow.invite}
      onSent={() => flow.setSentHere(true)}
      projects={flow.projects}
      setStep={flow.setStep}
      setTarget={flow.setTarget}
      status={flow.invite.status}
      step={flow.step}
      target={flow.target}
    />
  ) : null),
  start: startStep,
};

/** Escape closes the window, as in the other modals. */
const useEscape = (onClose: () => void) => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };

    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
};

const InvitePeopleContent: React.FC<Omit<InvitePeopleModalProps, 'isOpen'>> = ({ onClose, projectId, notice }) => {
  const flow = useInviteFlow(projectId, notice);
  const { batch, error } = flow.invite;

  useEscape(onClose);

  return (
    <div className="am-overlay" onClick={onClose}>
      <div aria-labelledby="invite-people-heading" aria-modal="true" className="am-modal invite-modal"
        onClick={(event) => event.stopPropagation()} role="dialog">
        <StepHeader onClose={onClose} step={headerStep(flow)} />
        <span className="am-sr-only" id="invite-people-heading">Invite people</span>
        {error && <p className="am-error" role="alert">{error}</p>}
        {flow.phase !== 'done' && batch?.error && <p className="am-error" role="alert">{batch.error}</p>}
        {BODIES[flow.phase](flow, onClose) ?? startStep(flow)}
      </div>
    </div>
  );
};

/** The "Invite people" window: where (a project and access, or 8080.AI), who (from Google contacts), then send. */
export const InvitePeopleModal: React.FC<InvitePeopleModalProps> = ({ isOpen, ...props }) => (
  isOpen ? createPortal(<InvitePeopleContent {...props} />, document.body) : null
);
